import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Breadcrumb, Icon } from "semantic-ui-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Components
import LocationsTable from "../molecules/LocationsTable";
import LocationForm from "../organisms/LocationForm";
// Redux
import api from "../../api";
// Themes
import "./PageStyles.scss";
import i18nstring, { strings } from "../../i18n";
import {
  createSchedule,
  createAddressWithResult,
  getSchedulesAndAddresses,
  setAddressToCreate,
  setScheduleToCreate,
  updateSchedule,
  updateAddressWithResult,
  updateSelectedAddressId,
  updateSelectedScheduleId,
  deleteSchedule,
  deleteAddress
} from "../../redux/actions/TimeWindow";
import Keys from "../../constants/Keys";
import { getSubscriptions } from "../../redux/actions/Subscription";
import { getLocales, updateLocale } from "../../redux/actions/Onboarding";
import { updateUserOffers } from "../../redux/actions/User";
import { HIDE_LOADING, SHOW_LOADING } from "../../redux/actions/Types";
import ConfirmationPopup from "../atoms/ConfirmationPopup";
import { EnablePickupDropoff } from "../molecules/EnablePickupDropoff";
import PickupInstructionsZips from "../molecules/PickupInstructionsZips";


const LocationsAndTimes = props => {
  const {
    user,
    subscriptions,
    selectedAddress,
    selectedSchedule,
    loadedSchedules,
    loadedAddresses,
    isLoading,
    attemptGetSchedulesAndAddresses,
    attemptGetSubscriptions,
    getLocales,
    attemptUpdateSelectedAddressId,
    attemptSetAddressToCreate,
    attemptSetScheduleToCreate,
    attemptUpdateSelectedSchedule,
    attemptCreateSchedule,
    attemptUpdateSchedule,
    attemptCreateAddressWithResult,
    attemptUpdateUserOffers,
    attemptUpdateAddressWithResult,
    locales } = props;
  
  const isAdmin = useMemo(() => user?.roles.includes("ADMIN") ?? false, [user]);
  const [locationEditorMode, setLocationEditorMode] = useState(null);
  const [showConfirmDeletionModal, setShowConfirmationModal] = useState(false);
  const [selectedAddressForDelete, setSelectedAddressForDelete] = useState();
  const [dropoff, setDropoff] = useState(user?.offers?.DROPOFF || false);
  const [pickup, setPickup] = useState(user?.offers?.PICKUP || false);
  const [offersDisabledInstructions, setOffersDisabledInstructions] = useState(user?.offersDisabledInstructions || "");
  const [offersModified, setOffersModified] = useState(false);
  const [isSavingOffers, setIsSavingOffers] = useState(false);
  const [isSavingPickupInstructions, setIsSavingPickupInstructions] = useState(false);
  const [isSavingPickupStates, setIsSavingPickupStates] = useState(false);
  const [isLoadingPickupInstructions, setIsLoadingPickupInstructions] = useState(false);
  const [pickupInstructions, setPickupInstructions] = useState([]);
  const availableZips = useMemo(
    () => subscriptions?.map(zip => zip.id) || [],
    [subscriptions]
  );
  const allSubscriptionZips = useMemo(
    () => (subscriptions || []).map(zip => String(zip.id)),
    [subscriptions]
  );

  const normalizePickupZipList = (zipList = []) =>
    Array.from(new Set((zipList || []).filter(Boolean).map(zip => String(zip))));

  const loadPickupInstructions = useCallback(async (operatorId = user?.id) => {
    if (!operatorId || isAdmin) {
      setPickupInstructions([]);
      return [];
    }

    setIsLoadingPickupInstructions(true);
    try {
      const result = await api.pickupInstruction.getPickupInstructions(operatorId);
      if (!result.success) {
        toast.error(result.error || i18nstring(strings.FAILED_TO_LOAD_PICKUP_INSTRUCTIONS));
        return [];
      }

      const nextInstructions = Array.isArray(result.data) ? result.data : [];
      setPickupInstructions(nextInstructions);
      return nextInstructions;
    } catch (error) {
      console.error(error);
      toast.error(i18nstring(strings.FAILED_TO_LOAD_PICKUP_INSTRUCTIONS));
      return [];
    } finally {
      setIsLoadingPickupInstructions(false);
    }
  }, [isAdmin, user?.id]);

  useEffect(() => {
    attemptGetSchedulesAndAddresses();

    if (!isAdmin) {
      attemptGetSubscriptions();
    }
  }, [attemptGetSchedulesAndAddresses, attemptGetSubscriptions, isAdmin]);

  useEffect(() => {
    if (!isAdmin && availableZips.length > 0) {
      getLocales(availableZips);
    }
  }, [isAdmin, availableZips, getLocales]);

  useEffect(() => {
    if (!isAdmin && user?.id) {
      loadPickupInstructions(user.id);
    }
  }, [isAdmin, user?.id, loadPickupInstructions]);

  const setScheduleForAddress = async deliverySchedule => {
    const selectedDays = {
      AM: deliverySchedule?.AM || [],
      PM: deliverySchedule?.PM || []
    };

    if (!selectedSchedule?.id) {
      return await attemptCreateSchedule({
        deliverySchedule: selectedDays,
        name: selectedSchedule?.name || ""
      });
    }

    const scheduleUpdated = await attemptUpdateSchedule(
      {
        id: selectedSchedule.id,
        name: selectedSchedule?.name,
        deliverySchedule: selectedDays
      },
      selectedAddress?.zips || []
    );
    return scheduleUpdated ? selectedSchedule.id : null;
  };

  const onSaveLocation = async data => {
    const isCreateAddress = selectedAddress?.address?.city === "";
    const fullAddressPayload = {
      ...(selectedAddress?.address || {}),
      ...(data?.address || {}),
      name: data?.name ?? selectedAddress?.name
    };

    if (isCreateAddress) {
      const currentSelectedSchedule = await setScheduleForAddress(data.deliverySchedule);
      if (!currentSelectedSchedule) {
        return false;
      }
      const addressPayload = {
        ...fullAddressPayload,
        currentSelectedSchedule
      };
      const createdAddressId = await attemptCreateAddressWithResult(addressPayload, currentSelectedSchedule);
      if (!createdAddressId) {
        return false;
      }
      setLocationEditorMode(null);
      if (data.selectedZips !== undefined) {
        const zipResult = await api.address.setAddressToZips(createdAddressId, data.selectedZips);
        if (!zipResult.success) {
          return false;
        }
      }
      return true;
    }

    const currentSelectedSchedule = await setScheduleForAddress(data.deliverySchedule);
    if (!currentSelectedSchedule) {
      return false;
    }

    const addressUpdated = await attemptUpdateAddressWithResult(selectedAddress.id, fullAddressPayload);
    if (!addressUpdated) {
      return false;
    }
    if (data.selectedZips !== undefined) {
      const zipResult = await api.address.setAddressToZips(selectedAddress.id, data.selectedZips);
      if (!zipResult.success) {
        return false;
      }
    }

    return true;
  };

  const onEditAddress = address => {
    attemptUpdateSelectedAddressId(address.key);

    const scheduleIndex = loadedSchedules.findIndex(
      schedule => schedule.id === address.schedule
    );
    if (scheduleIndex >= 0) {
      attemptUpdateSelectedSchedule(scheduleIndex);
    }

    setLocationEditorMode("edit");
  };

  const onCreateAddress = () => {
    attemptUpdateSelectedAddressId(Keys.NEW_ITEM);
    attemptSetAddressToCreate();
    attemptSetScheduleToCreate();
    setLocationEditorMode("create");
  };

  const onBackToLocations = () => {
    setLocationEditorMode(null);
  };

  const onLocationSaveComplete = async () => {
    const wasCreateMode = locationEditorMode === "create";
    const savedAddressId = locationEditorMode === "edit" ? selectedAddress?.id : null;
    if (locationEditorMode === "create") {
      onBackToLocations();
    }

    if (!wasCreateMode) {
      toast.success(i18nstring(strings.LOCATION_UPDATED_SUCCESSFULLY));
    }
    await attemptGetSchedulesAndAddresses(false, savedAddressId, true);
    if (wasCreateMode) {
      toast.success(i18nstring(strings.LOCATION_ADDED_SUCCESSFULLY));
    }
  };

  const deleteAddress = () => {
    setShowConfirmationModal(false);
    if (selectedAddressForDelete?.schedule) {
      props.attemptDeleteSchedule(selectedAddressForDelete.schedule);
      props.attemptDeleteAddress(selectedAddressForDelete.id);
    }
  };

  const handleOffersDropoffChange = (value) => {
    setDropoff(value);
    setOffersModified(true);
  };

  const handleOffersPickupChange = (value) => {
    setPickup(value);
    setOffersModified(true);
  };

  const handleOffersInstructionsChange = (value) => {
    setOffersDisabledInstructions(value);
    setOffersModified(true);
  };

  const handleSaveOffers = async () => {
    if (!offersModified || isSavingOffers) return;
    setIsSavingOffers(true);
    try {
      await attemptUpdateUserOffers(
        { DROPOFF: dropoff, PICKUP: pickup },
        offersDisabledInstructions
      );
      setOffersModified(false);
    } finally {
      setIsSavingOffers(false);
    }
  };

  const syncPickupLocalesForInstruction = async ({
    zips: instructionZips = [],
    deliveryInstructions,
    deliverySchedule,
    pickupEnabled
  }) => {
    const localeMap = new Map(
      (locales || []).map(locale => [String(locale.zipCode), locale])
    );

    for (const zipCode of normalizePickupZipList(instructionZips)) {
      const existingLocale = localeMap.get(zipCode);
      const result = await api.locale.updateLocale({
        zipCode,
        deliverySchedule:
          deliverySchedule ?? existingLocale?.deliverySchedule ?? { AM: [], PM: [] },
        deliveryInstructions:
          deliveryInstructions ?? existingLocale?.deliveryInstructions ?? "",
        pickupEnabled:
          pickupEnabled !== undefined
            ? pickupEnabled
            : existingLocale?.pickupEnabled === true
      });

      if (!result.success) {
        return result;
      }
    }

    return { success: true };
  };

  const clearPickupLocalesForInstruction = async (zipList = []) => {
    const localeMap = new Map(
      (locales || []).map(locale => [String(locale.zipCode), locale])
    );

    for (const zipCode of normalizePickupZipList(zipList)) {
      const existingLocale = localeMap.get(zipCode);
      const result = await api.locale.updateLocale({
        zipCode,
        deliverySchedule: { AM: [], PM: [] },
        deliveryInstructions: "",
        pickupEnabled: existingLocale?.pickupEnabled === true
      });

      if (!result.success) {
        return result;
      }
    }

    return { success: true };
  };

  const handleSavePickupInstruction = async (instructionForm, instructionId = null) => {
    if (isSavingPickupInstructions) {
      return false;
    }

    const normalizedZips = normalizePickupZipList(instructionForm?.zips || []);
    const payload = {
      deliveryInstructions: (instructionForm?.deliveryInstructions || "").trim(),
      deliverySchedule: {
        AM: instructionForm?.deliverySchedule?.AM || [],
        PM: instructionForm?.deliverySchedule?.PM || []
      },
      zips: normalizedZips
    };
    const previousInstruction = pickupInstructions.find(
      instruction => instruction.id === instructionId
    );
    const previousZips = normalizePickupZipList(previousInstruction?.zips || []);

    setIsSavingPickupInstructions(true);
    setIsLoadingPickupInstructions(true);
    try {
      const result = instructionId
        ? await api.pickupInstruction.updatePickupInstruction(instructionId, payload)
        : await api.pickupInstruction.createPickupInstruction(payload);

      if (!result.success) {
        toast.error(
          result.error ||
            i18nstring(
              instructionId
                ? strings.FAILED_TO_UPDATE_PICKUP_INSTRUCTION
                : strings.FAILED_TO_CREATE_PICKUP_INSTRUCTION
            )
        );
        return false;
      }

      const savedInstruction = result.data && typeof result.data === "object"
        ? result.data
        : payload;
      const currentZips = normalizePickupZipList(savedInstruction?.zips || payload.zips);
      const removedZips = previousZips.filter(zip => !currentZips.includes(zip));

      const localeSyncResult = await syncPickupLocalesForInstruction({
        ...payload,
        ...savedInstruction,
        zips: currentZips
      });

      if (!localeSyncResult.success) {
        toast.error(
          localeSyncResult.error || i18nstring(strings.FAILED_TO_UPDATE_PICKUP_ENABLED_STATUS)
        );
        return false;
      }

      if (removedZips.length > 0) {
        const clearResult = await clearPickupLocalesForInstruction(removedZips);
        if (!clearResult.success) {
          toast.error(
            clearResult.error ||
              i18nstring(strings.FAILED_TO_REMOVE_OLD_PICKUP_INSTRUCTION_DATA)
          );
          return false;
        }
      }

      await loadPickupInstructions(user?.id);

      if (allSubscriptionZips.length > 0) {
        await getLocales(allSubscriptionZips, true, false);
      }

      toast.success(
        i18nstring(
          instructionId
            ? strings.PICKUP_INSTRUCTION_UPDATED_SUCCESSFULLY
            : strings.PICKUP_INSTRUCTION_CREATED_SUCCESSFULLY
        )
      );
      return true;
    } finally {
      setIsSavingPickupInstructions(false);
      setIsLoadingPickupInstructions(false);
    }
  };

  const handleSavePickupStates = async (pickupStateChanges = []) => {
    if (
      isSavingPickupInstructions ||
      isSavingPickupStates ||
      pickupStateChanges.length === 0
    ) {
      return false;
    }

    const batchLocales = pickupStateChanges.map(change => ({
      id: String(change.zipCode),
      pickupEnabled: change.pickupEnabled === true
    }));

    setIsSavingPickupStates(true);
    try {
      const result = await api.locale.updateLocalesBatch(batchLocales);

      if (!result.success) {
        toast.error(
          result.error || i18nstring(strings.FAILED_TO_UPDATE_PICKUP_ENABLED_STATUS)
        );
        return false;
      }

      if (allSubscriptionZips.length > 0) {
        await getLocales(allSubscriptionZips, true, false);
      }

      toast.success(i18nstring(strings.PICKUP_ENABLED_STATUS_UPDATED_SUCCESSFULLY));
      return true;
    } catch (error) {
      console.error(error);
      toast.error(i18nstring(strings.FAILED_TO_UPDATE_PICKUP_ENABLED_STATUS));
      return false;
    } finally {
      setIsSavingPickupStates(false);
    }
  };

  const handleDeletePickupInstruction = async (instructionId) => {
    if (!instructionId || isSavingPickupInstructions) {
      return false;
    }

    const deletedInstruction = pickupInstructions.find(
      instruction => instruction.id === instructionId
    );

    setIsSavingPickupInstructions(true);
    setIsLoadingPickupInstructions(true);
    try {
      const result = await api.pickupInstruction.deletePickupInstruction(instructionId);
      if (!result.success) {
        toast.error(result.error || i18nstring(strings.FAILED_TO_DELETE_PICKUP_INSTRUCTION));
        return false;
      }

      const clearResult = await clearPickupLocalesForInstruction(
        deletedInstruction?.zips || []
      );
      if (!clearResult.success) {
        toast.error(
          clearResult.error ||
            i18nstring(strings.FAILED_TO_REMOVE_DELETED_PICKUP_INSTRUCTION_DATA)
        );
        return false;
      }

      await loadPickupInstructions(user?.id);
      if (allSubscriptionZips.length > 0) {
        await getLocales(allSubscriptionZips, true, false);
      }

      toast.success(i18nstring(strings.PICKUP_INSTRUCTION_DELETED_SUCCESSFULLY));
      return true;
    } catch (error) {
      console.error(error);
      toast.error(i18nstring(strings.FAILED_TO_DELETE_PICKUP_INSTRUCTION));
      return false;
    } finally {
      setIsSavingPickupInstructions(false);
      setIsLoadingPickupInstructions(false);
    }
  };

  const isEditorMode = useMemo(
    () => locationEditorMode === "create" || locationEditorMode === "edit",
    [locationEditorMode]
  );

  useEffect(() => {
    if (!isEditorMode) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [isEditorMode, selectedAddress?.id]);
  
  if (isLoading)
    return null;

  const renderListView = () => (
    <div className="add_edit_window">
      {showConfirmDeletionModal && (
        <ConfirmationPopup
          title={i18nstring(strings.CONFIRMATION)}
          message={i18nstring(strings.DELETE_LOC_CONFIRMATION_MESSAGE)}
          primaryButtonText={i18nstring(strings.CONFIRM)}
          onPrimaryButtonClicked={deleteAddress}
          open={showConfirmDeletionModal}
          setOpen={setShowConfirmationModal}
        />
      )}
      <div className="pickup_window add_edit_address_window">
        <div className="pickup_window_content">
          <EnablePickupDropoff
            save
            dropoff={dropoff}
            pickup={pickup}
            offersDisabledInstructions={offersDisabledInstructions}
            onDropoffChange={handleOffersDropoffChange}
            onPickupChange={handleOffersPickupChange}
            onInstructionsChange={handleOffersInstructionsChange}
            isSaveDisabled={!offersModified || isSavingOffers}
            onSave={handleSaveOffers}
          />
        </div>
        <div className="pickup_window_content">
          {loadedAddresses.length > 0 && (
            <LocationsTable
              loadedAddresses={loadedAddresses}
              loadedSchedules={loadedSchedules}
              onEdit={onEditAddress}
              onCreate={onCreateAddress}
              onDelete={address => {
                setSelectedAddressForDelete(address);
                setShowConfirmationModal(true);
              }}
              onSelectAddress={address => setSelectedAddressForDelete(address)}
            />
          )}
        </div>
        <div className="add_edit_address_window">
          <div className="pickup_window_content">
            <PickupInstructionsZips
              zips={availableZips}
              locales={locales}
              pickupInstructions={pickupInstructions}
              isSavingInstructions={isSavingPickupInstructions}
              isSavingPickupStates={isSavingPickupStates}
              isLoadingInstructions={isLoadingPickupInstructions}
              onSaveInstruction={handleSavePickupInstruction}
              onDeleteInstruction={handleDeletePickupInstruction}
              onSavePickupStates={handleSavePickupStates}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEditorView = () => (
    <div className="add_edit_window">
      <div className="pickup_window add_edit_address_window">
        <div className="pickup_window_content">
          <div className="locations_breadcrumb_row">
            <Breadcrumb>
              <Breadcrumb.Section link onClick={onBackToLocations}>
                {i18nstring(strings.LOCATIONS_HEADING)}
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon="right chevron" />
              <Breadcrumb.Section active={locationEditorMode === "create"}>
                {locationEditorMode === "create"
                  ? i18nstring(strings.ADD_LOCATION)
                  : i18nstring(strings.EDIT_LOCATION)}
              </Breadcrumb.Section>
              {locationEditorMode === "edit" && selectedAddress?.name && (
                <>
                  <Breadcrumb.Divider icon="right chevron" />
                  <Breadcrumb.Section active>
                    {selectedAddress.name}
                  </Breadcrumb.Section>
                </>
              )}
            </Breadcrumb>
          </div>
          <div
            className="locations_back_button"
            onClick={onBackToLocations}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onBackToLocations();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <Icon name="arrow left" />
            <span>{i18nstring(strings.GO_BACK)}</span>
          </div>
        </div>

        <LocationForm
          selectedAddress={selectedAddress}
          selectedSchedule={selectedSchedule}
          availableZips={availableZips}
          onSave={onSaveLocation}
          onSaveComplete={onLocationSaveComplete}
          onCancel={onBackToLocations}
        />
      </div>
    </div>
  );

  return (
    <div className="main_container_section">
      <ToastContainer position="top-right" autoClose={3000} />
      {!isEditorMode && renderListView()}
      {isEditorMode && selectedAddress && renderEditorView()}
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.account.user,
  subscriptions: state.subscription.subscriptions,
  selectedSchedule: state.timeWindow.selectedSchedule,
  selectedAddress: state.timeWindow.selectedAddress,
  loadedSchedules: state.timeWindow.loadedSchedules,
  loadedAddresses: state.timeWindow.loadedAddresses,
  isLoading: state.onboarding.isLoading,
  locales: state.onboarding.locales,
});

const mapDispatchToProps = dispatch => {
  return {
    attemptGetSubscriptions: () => dispatch(getSubscriptions()),
    attemptGetSchedulesAndAddresses: (clear = true, savedAddressId, silent) => dispatch(getSchedulesAndAddresses(clear, savedAddressId, silent)),
    attemptSetScheduleToCreate: () => dispatch(setScheduleToCreate()),
    attemptSetAddressToCreate: () => dispatch(setAddressToCreate()),
    attemptUpdateSelectedSchedule: selectedScheduleId =>
      dispatch(updateSelectedScheduleId(selectedScheduleId)),
    attemptCreateSchedule: schedule => dispatch(createSchedule(schedule)),
    attemptUpdateSchedule: (schedule, zips) =>
      dispatch(updateSchedule(schedule, zips)),
    attemptUpdateSelectedAddressId: selectedAddressId =>
      dispatch(updateSelectedAddressId(selectedAddressId)),
    attemptCreateAddressWithResult: (address, currentSelectedSchedule) =>
      dispatch(createAddressWithResult(address, currentSelectedSchedule)),
    attemptUpdateAddressWithResult: (addressId, address, currentSelectedSchedule) =>
      dispatch(updateAddressWithResult(addressId, address, currentSelectedSchedule)),
    getLocales: (zips, silent = false, clear = true) =>
      dispatch(getLocales(zips, silent, clear)),
    attemptUpdateUserOffers: (offers, instructions) =>
      dispatch(updateUserOffers(offers, instructions)),
    showLoading: () => dispatch({ type: SHOW_LOADING }),
    hideLoading: () => dispatch({ type: HIDE_LOADING }),
    attemptDeleteSchedule: scheduleId => dispatch(deleteSchedule(scheduleId)),
    attemptDeleteAddress: addressId => dispatch(deleteAddress(addressId)),
    attemptUpdateLocale: (zips, locale, silent = false, clear = true) => dispatch(updateLocale(zips, locale, silent, clear)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationsAndTimes);
